import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/migration/add';
import Select from '@atlaskit/select';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { CONDITION_MATCH_TYPES, DEFAULT_FIELDS, FIELD_MAPPINGS } from '../../common/constants';
import { ConditionFilterMessages } from '../../common/messages';
import { type ConditionUpdater } from '../../common/types';

import Condition from './condition';
import { AddConditionButton, ConditionsWrapper } from './styled';
import { type IConditionFilterProps, type IFilterValues } from './types';
import { getEntries } from './utils';

export const ConditionFilter: React.FC<IConditionFilterProps> = ({
	value,
	onChange: update,
	fields = DEFAULT_FIELDS,
	isFieldKeyRequired,
	isDisabled,
	isReadOnly,
	id,
}) => {
	const { formatMessage } = useIntl();

	const [didAddNewCondition, setDidAddNewCondition] = useState<boolean>(false);
	const matchType = CONDITION_MATCH_TYPES.find((type) => type.value === value.matchType);

	const updateCondition = useCallback(
		(index: number): ConditionUpdater =>
			(record) => {
				const _state = JSON.parse(JSON.stringify(value)) as IFilterValues;

				for (const [key, value] of getEntries(record)) {
					_state.conditions[index][key] = value;
				}

				update(_state);
			},
		[update, value],
	);

	const deleteCondition = (index: number) => () => {
		const _state = JSON.parse(JSON.stringify(value)) as IFilterValues;
		_state.conditions.splice(index, 1);
		update(_state);
	};

	const AddNewCondition = () =>
		update({
			...value,
			conditions: [
				...value.conditions,
				{
					field: fields[0].value,
					expectedValue: '',
					not: false,
					operation: FIELD_MAPPINGS[fields[0].type]?.default.value ?? 'contains',
					fieldFreeForm: fields[0].value === '',
				},
			],
		});

	return (
		<>
			<Select
				id="condition-filter-match-type"
				inputId={id}
				options={CONDITION_MATCH_TYPES}
				defaultValue={matchType}
				isDisabled={isDisabled}
				menuIsOpen={isReadOnly ? false : undefined}
				isSearchable={!isReadOnly}
				onChange={(match_type) => {
					if (match_type) {
						// If there isn't a condition and match-all is not selected, add a default condition
						if (match_type.value !== 'match-all' && value.conditions.length === 0) {
							update({
								conditions: [
									{
										field: fields[0].value,
										expectedValue: '',
										not: false,
										operation: FIELD_MAPPINGS[fields[0].type]?.default.value ?? 'contains',
										fieldFreeForm: fields[0].value === '',
									},
								],
								matchType: match_type.value,
							});
							return;
						}

						// If match-all is selected, clear the previously added conditions
						if (match_type.value === 'match-all') {
							update({ conditions: [], matchType: match_type.value });
							return;
						}

						update({ ...value, matchType: match_type.value });
						return;
					}
				}}
				menuPosition="fixed"
			/>
			{value.matchType !== 'match-all' && (
				<>
					<ConditionsWrapper>
						{value.conditions.map((cond, idx) => (
							<Condition
								key={idx}
								fields={fields}
								update={updateCondition(idx)}
								delete={deleteCondition(idx)}
								conditionCount={value.conditions.length}
								isFieldKeyRequired={isFieldKeyRequired}
								isDisabled={isDisabled}
								isReadOnly={isReadOnly}
								autoFocus={didAddNewCondition && idx === value.conditions.length - 1}
								{...cond}
							/>
						))}
					</ConditionsWrapper>
					<AddConditionButton
						appearance="link"
						iconBefore={<AddIcon LEGACY_size="small" label="" color="currentColor" />}
						onClick={(e, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'addFilterCondition');
							AddNewCondition();
							setDidAddNewCondition(true);
						}}
						isDisabled={isDisabled || isReadOnly}
					>
						{formatMessage(ConditionFilterMessages.AddNewCondition)}
					</AddConditionButton>
				</>
			)}
		</>
	);
};
